import { useTranslation } from 'react-i18next';
import { useExternalContact } from '@/api/queries/useExternalContacts';
import { notFoundOrUnknownKey } from '@/api/errors';
import { useAuthStore } from '@/stores/auth-store';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { BackButton } from '@/components/back-button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  EntityDetailTitle,
  Skeleton,
  Page,
  PageContent,
  formatDate,
  renderFallback
} from '@bliks-io/ui';

interface ExternalContactDetailPageProps {
  externalContactId: string;
}

export function ExternalContactDetailPage({ externalContactId }: ExternalContactDetailPageProps) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const { data: contact, isPending, error } = useExternalContact(externalContactId);

  useDocumentTitle('pageTitles.externalContactDetail', { name: contact?.name || 'Loading...' });

  if (isPending) {
    return (
      <Page>
        <PageContent>
          <Skeleton className="h-8 w-64 mb-8" />
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        </PageContent>
      </Page>
    );
  }

  if (error || !contact) {
    return (
      <Page>
        <PageContent>
          <div className="flex items-center gap-4 mb-8">
            <BackButton to="/$slug/organization/external-contacts" params={{ slug }} />
          </div>
          <Card>
            <CardContent className="p-6">
              <p className="text-destructive">
                {t(notFoundOrUnknownKey(error, 'organization.contacts.notFound'))}
              </p>
            </CardContent>
          </Card>
        </PageContent>
      </Page>
    );
  }

  return (
    <Page>
      <PageContent>
        <div className="flex items-center gap-4 mb-8">
          <BackButton to="/$slug/organization/external-contacts" params={{ slug }} />
          <div>
            <EntityDetailTitle name={contact.name} deletedAt={contact.deletedAt} />
            <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
              {t('organization.contacts.detailDescription')}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('organization.contacts.details')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-8">{t('common.Name')}</label>
                <p className="mt-1 text-base">{contact.name}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-8">{t('common.Email')}</label>
                <p className="mt-1 text-base">{renderFallback(contact.email)}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-8">{t('common.Type')}</label>
                <div className="mt-1">
                  <Badge variant="secondary">{contact.type}</Badge>
                </div>
              </div>

              {contact.description && (
                <div>
                  <label className="text-sm font-medium text-gray-8">
                    {t('common.Description')}
                  </label>
                  <p className="mt-1 text-base">{contact.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <label className="text-sm font-medium text-gray-8">{t('common.Created')}</label>
                  <p className="mt-1 text-base">{formatDate(contact.createdAt)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-8">{t('common.Updated')}</label>
                  <p className="mt-1 text-base">{formatDate(contact.updatedAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContent>
    </Page>
  );
}
